import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';
import { FILTER_VALUES, NOTIFICATION_SCREEN, PAGINATION } from '../shared/appConstants';
import { normalizeNotifications } from '../view/notifications/notificationUtils';

const DEFAULT_PAGINATION = {
  page: PAGINATION.DEFAULT_PAGE,
  limit: PAGINATION.DEFAULT_LIMIT,
  hasMore: true,
  loading: false
};

const TAB_KEYS = {
  UNREAD: NOTIFICATION_SCREEN.TABS.UNREAD,
  CLEARED: NOTIFICATION_SCREEN.TABS.CLEARED
};

const getTabKey = (tab) => (
  tab === TAB_KEYS.CLEARED ? TAB_KEYS.CLEARED : TAB_KEYS.UNREAD
);

const mergeById = (existing, incoming) => {
  const map = new Map();
  existing.forEach(item => {
    if (item?.id) {
      map.set(item.id, item);
    }
  });
  incoming.forEach(item => {
    if (item?.id) {
      map.set(item.id, item);
    }
  });
  return Array.from(map.values());
};

const getGroupedItemKey = (item, index = 0) => {
  if (item?.id) return `id-${item.id}`;
  if (item?.parent_comment_id) return `parent-${item.parent_comment_id}`;
  return `index-${index}`;
};

const mergeRepliesById = (existingReplies = [], incomingReplies = []) => {
  const replyMap = new Map();

  existingReplies.forEach((reply, index) => {
    const key = reply?.id ? `id-${reply.id}` : `index-${index}`;
    replyMap.set(key, reply);
  });

  incomingReplies.forEach((reply, index) => {
    const key = reply?.id ? `id-${reply.id}` : `incoming-${index}`;
    replyMap.set(key, reply);
  });

  return Array.from(replyMap.values());
};

const mergeGroupedItems = (existingItems = [], incomingItems = []) => {
  const itemMap = new Map();

  existingItems.forEach((item, index) => {
    itemMap.set(getGroupedItemKey(item, index), item);
  });

  incomingItems.forEach((item, index) => {
    const key = getGroupedItemKey(item, index);
    const existing = itemMap.get(key);

    if (existing?.replies && item?.replies) {
      itemMap.set(key, {
        ...existing,
        ...item,
        replies: mergeRepliesById(existing.replies, item.replies)
      });
      return;
    }

    itemMap.set(key, item);
  });

  return Array.from(itemMap.values());
};

const extractGroupedNotifications = (groups = [], idSet, isRead) => {
  const remainingGroups = [];
  const movedGroups = [];

  groups.forEach((group) => {
    const remainingItems = [];
    const movedItems = [];

    (group?.items || []).forEach((item) => {
      if (item?.id && idSet.has(item.id)) {
        movedItems.push({ ...item, is_read: isRead });
        return;
      }

      if (Array.isArray(item?.replies) && item.replies.length > 0) {
        const movedReplies = item.replies
          .filter((reply) => idSet.has(reply?.id))
          .map((reply) => ({ ...reply, is_read: isRead }));
        const remainingReplies = item.replies
          .filter((reply) => !idSet.has(reply?.id));

        if (movedReplies.length > 0) {
          movedItems.push({
            ...item,
            replies: movedReplies
          });
        }

        if (remainingReplies.length > 0 || item?.id) {
          remainingItems.push({
            ...item,
            replies: remainingReplies
          });
        }
        return;
      }

      remainingItems.push(item);
    });

    if (remainingItems.length > 0) {
      remainingGroups.push({
        ...group,
        items: remainingItems
      });
    }

    if (movedItems.length > 0) {
      movedGroups.push({
        ...group,
        items: movedItems
      });
    }
  });

  return { remainingGroups, movedGroups };
};

const getGroupId = (group) => group?.ticket_id || group?.sprint_id || group?.group_by;

const mergeGroupedCollections = (existingGroups = [], incomingGroups = []) => {
  const groupMap = new Map();

  existingGroups.forEach((group) => {
    const groupId = getGroupId(group);
    if (groupId) {
      groupMap.set(groupId, group);
    }
  });

  incomingGroups.forEach((group) => {
    const groupId = getGroupId(group);
    if (!groupId) return;
    const existing = groupMap.get(groupId);
    if (!existing) {
      groupMap.set(groupId, group);
      return;
    }

    groupMap.set(groupId, {
      ...existing,
      ...group,
      items: mergeGroupedItems(existing.items, group.items)
    });
  });

  return Array.from(groupMap.values());
};

const moveNotificationsBetweenTabs = (state, ids = [], isRead = true) => {
  if (!Array.isArray(ids) || ids.length === 0) return;

  const sourceTab = isRead ? TAB_KEYS.UNREAD : TAB_KEYS.CLEARED;
  const targetTab = isRead ? TAB_KEYS.CLEARED : TAB_KEYS.UNREAD;
  const sourceItems = state.itemsByTab[sourceTab];
  const idSet = new Set(ids);
  const isGrouped = sourceItems.length > 0 && getGroupId(sourceItems[0]) && Array.isArray(sourceItems[0]?.items);

  if (isGrouped) {
    const { remainingGroups, movedGroups } = extractGroupedNotifications(sourceItems, idSet, isRead);
    state.itemsByTab[sourceTab] = remainingGroups;
    if (movedGroups.length > 0) {
      state.itemsByTab[targetTab] = mergeGroupedCollections(movedGroups, state.itemsByTab[targetTab]);
    }
  } else {
    const itemsToMove = sourceItems
      .filter((item) => idSet.has(item?.id))
      .map((item) => ({ ...item, isRead }));

    state.itemsByTab[sourceTab] = sourceItems.filter((item) => !idSet.has(item?.id));

    if (itemsToMove.length > 0) {
      state.itemsByTab[targetTab] = [
        ...itemsToMove,
        ...state.itemsByTab[targetTab]
      ];
    }
  }

  if (isRead) {
    state.unreadCount = Math.max(0, state.unreadCount - ids.length);
  } else {
    state.unreadCount += ids.length;
  }
};

export const fetchNotificationsByUser = createAsyncThunk(
  'notifications/fetchNotificationsByUser',
  async ({
    userId,
    tab = TAB_KEYS.UNREAD,
    unreadOnly,
    page = PAGINATION.DEFAULT_PAGE,
    limit = PAGINATION.DEFAULT_LIMIT,
    priority,
    projectId,
    assignee,
    startDate,
    endDate,
    order,
    groupBy,
    append = false
  }, { rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      const params = new URLSearchParams({ page, limit });
      if (unreadOnly === true) {
        params.append('unread', 'true');
      } else if (unreadOnly === false) {
        params.append('unread', 'false');
      }
      if (priority && priority !== FILTER_VALUES.ALL) params.append('priority', priority);
      if (projectId && projectId !== FILTER_VALUES.ALL) params.append('project_id', projectId);
      if (assignee && assignee !== FILTER_VALUES.ALL) params.append('assignee', assignee);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (order) params.append('order', order);
      if (groupBy) params.append('group_by', groupBy);

      const response = await api.get(`${ENDPOINTS.NOTIFICATIONS_BY_USER(userId)}?${params.toString()}`);
      const payload = response?.data?.data || {};
      const groupedItems = Array.isArray(payload.grouped_items) ? payload.grouped_items : [];
      const items = groupedItems.length > 0 ? groupedItems : (Array.isArray(payload.items) ? payload.items : []);
      const pagination = payload.pagination || { page, limit };
      const unreadCount = typeof payload.unread_count === 'number' ? payload.unread_count : 0;

      return {
        tab: getTabKey(tab),
        items,
        pagination,
        unreadCount,
        append,
        isGrouped: groupedItems.length > 0
      };
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async ({ userId, projectId } = {}, { rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      let url = ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT(userId);
      if (projectId && projectId !== 'all' && projectId !== 'undefined' && projectId !== 'null') {
         url += `?project_id=${projectId}`;
      }
      const response = await api.get(url);
      const unreadCount = response?.data?.data?.unread_count || 0;
      return { count: unreadCount, projectId };
    } catch (error) {
      // Silently fail - don't block UI
      return rejectWithValue(0);
    }
  }
);

export const markNotificationsRead = createAsyncThunk(
  'notifications/markNotificationsRead',
  async ({ notificationIds, ticketId, is_read = true }, { rejectWithValue }) => {
    try {
      const notificationIdArray = Array.isArray(notificationIds) ? notificationIds : [notificationIds].filter(Boolean);
      const params = new URLSearchParams();
      params.append('is_read', is_read);
      
      const payload = ticketId
        ? { ticket_id: ticketId }
        : { notification_id: notificationIdArray };
      
      const response = await api.patch(`${ENDPOINTS.NOTIFICATIONS_READ}?${params.toString()}`, payload);
      return { notificationIds: notificationIdArray, ticketId, is_read, data: response?.data?.data };
    } catch (error) {
      const apiError = error.response?.data?.error || error.message;
      return rejectWithValue(apiError);
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    itemsByTab: {
      [TAB_KEYS.UNREAD]: [],
      [TAB_KEYS.CLEARED]: []
    },
    paginationByTab: {
      [TAB_KEYS.UNREAD]: { ...DEFAULT_PAGINATION },
      [TAB_KEYS.CLEARED]: { ...DEFAULT_PAGINATION }
    },
    errorByTab: {
      [TAB_KEYS.UNREAD]: null,
      [TAB_KEYS.CLEARED]: null
    },
    unreadCount: 0,
    unreadCountByProject: {}
  },
  reducers: {
    resetNotificationsTab: (state, action) => {
      const tab = getTabKey(action.payload);
      state.itemsByTab[tab] = [];
      state.paginationByTab[tab] = { ...DEFAULT_PAGINATION };
      state.errorByTab[tab] = null;
    },
    setUnreadCount: (state, action) => {
      if (typeof action.payload === 'object' && action.payload !== null) {
        const { count, projectId } = action.payload;
        const nextCount = Number(count);
        if (Number.isFinite(nextCount)) {
          if (projectId && projectId !== 'all') {
            state.unreadCountByProject[projectId] = Math.max(0, nextCount);
          } else {
            state.unreadCount = Math.max(0, nextCount);
          }
        }
      } else {
        const nextCount = Number(action.payload);
        state.unreadCount = Number.isFinite(nextCount) ? Math.max(0, nextCount) : state.unreadCount;
      }
    },
    applyNotificationReadState: (state, action) => {
      const ids = Array.isArray(action.payload?.notificationIds)
        ? action.payload.notificationIds
        : [];
      const isRead = action.payload?.is_read !== false;

      moveNotificationsBetweenTabs(state, ids, isRead);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsByUser.pending, (state, action) => {
        const tab = getTabKey(action.meta.arg?.tab);
        state.paginationByTab[tab].loading = true;
        state.errorByTab[tab] = null;
      })
      .addCase(fetchNotificationsByUser.fulfilled, (state, action) => {
        const { tab, items, pagination, unreadCount, append, isGrouped } = action.payload || {};
        const tabKey = getTabKey(tab);
        
        let processedItems;
        if (isGrouped) {
          // Items are already filtered by the API based on unread parameter
          processedItems = items;
        } else {
          // Normalize flat items
          processedItems = normalizeNotifications(items);
        }
        
        const receivedCount = Array.isArray(items) ? items.length : 0;
        const limit = pagination?.limit || DEFAULT_PAGINATION.limit;
        const pageNumber = pagination?.page || DEFAULT_PAGINATION.page;
        const totalPages = pagination?.total_pages;
        const totalCount = pagination?.total_count;
        const hasMore = typeof totalPages === 'number'
          ? pageNumber < totalPages
          : typeof totalCount === 'number'
            ? (pageNumber * limit) < totalCount
            : receivedCount >= limit;

        if (append) {
          if (isGrouped) {
            // Merge groups by their ID (ticket_id, sprint_id, etc.)
            const existingGroups = new Map();
            state.itemsByTab[tabKey].forEach(group => {
              const groupId = getGroupId(group);
              if (groupId) {
                existingGroups.set(groupId, group);
              }
            });
            
            processedItems.forEach(newGroup => {
              const groupId = getGroupId(newGroup);
              if (groupId) {
                const existing = existingGroups.get(groupId);
                if (existing) {
                  // Merge items into existing group - need to handle items with and without id
                  const existingItemsMap = new Map();
                  
                  existing.items.forEach((item, index) => {
                    if (item.id) {
                      existingItemsMap.set(item.id, item);
                    } else if (item.parent_comment_id) {
                      // For items without id but with parent_comment_id (reply containers)
                      existingItemsMap.set(`parent-${item.parent_comment_id}`, item);
                    } else {
                      // Fallback: use index-based key
                      existingItemsMap.set(`index-${index}`, item);
                    }
                  });
                  
                  newGroup.items.forEach((item, index) => {
                    if (item.id) {
                      existingItemsMap.set(item.id, item);
                    } else if (item.parent_comment_id) {
                      existingItemsMap.set(`parent-${item.parent_comment_id}`, item);
                    } else {
                      existingItemsMap.set(`new-index-${index}`, item);
                    }
                  });
                  
                  existing.items = Array.from(existingItemsMap.values());
                } else {
                  // Add new group
                  existingGroups.set(groupId, newGroup);
                }
              }
            });
            
            state.itemsByTab[tabKey] = Array.from(existingGroups.values());
          } else {
            state.itemsByTab[tabKey] = mergeById(state.itemsByTab[tabKey], processedItems);
          }
        } else {
          state.itemsByTab[tabKey] = processedItems;
        }
        state.paginationByTab[tabKey] = {
          page: pageNumber,
          limit,
          hasMore,
          loading: false
        };
        const projectId = action.meta.arg?.projectId;
        if (projectId && projectId !== 'all') {
          state.unreadCountByProject[projectId] = unreadCount;
        } else {
          state.unreadCount = unreadCount;
        }
      })
      .addCase(fetchNotificationsByUser.rejected, (state, action) => {
        const tab = getTabKey(action.meta.arg?.tab);
        state.paginationByTab[tab].loading = false;
        state.errorByTab[tab] = action.payload || action.error?.message || null;
      })
      .addCase(markNotificationsRead.fulfilled, () => {})
      .addCase(markNotificationsRead.rejected, (state, action) => {
        state.errorByTab[TAB_KEYS.UNREAD] = action.payload || action.error?.message || null;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        const payload = action.payload;
        const { count, projectId } = typeof payload === 'object' && payload !== null
          ? payload
          : { count: payload, projectId: undefined };
        const numericCount = Number(count);
        const safeCount = Number.isFinite(numericCount) ? Math.max(0, numericCount) : 0;
        if (projectId && projectId !== 'all') {
          state.unreadCountByProject[projectId] = safeCount;
        } else {
          state.unreadCount = safeCount;
        }
      });
  }
});

export const { resetNotificationsTab, setUnreadCount, applyNotificationReadState } = notificationSlice.actions;
export default notificationSlice.reducer;
